import { SubmissionType } from '@common/enums/SubmissionType';
import { areHeraldProductsEligibleToCreateSubmission } from '@common/utils';
import { compact, isEmpty, lowerCase } from 'lodash';
import { DeleteOutline, EmailOutlined, Launch, SettingsEthernetOutlined } from '@icons';
import { PlacementType, QuoteFileIdType, SubmissionMarketRequestStatus, SubmissionMethod } from 'enums';
import { messages } from 'i18n';
import theme from 'themes';
import { ExtendedMarketRequest } from 'types';
import { MenuGroup, MenuItemProps } from 'components/menu';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { useSubmissionMarketRequestActions } from './useSubmissionMarketRequestActions';

export default function useDropdownMenuItems(
  marketRequest: ExtendedMarketRequest,
  setIsSubmittingMarketRequest?: (isSubmitting: boolean) => void,
): MenuGroup[] {
  const { submission } = useSubmissionsWorkspace();
  const {
    submitToMarketByEmail,
    submitToMarketByPortal,
    submitProductViaApi,
    changeToAwaitingQuote,
    openInformationRequested,
    attachQuote,
    editQuote,
    viewQuote,
    viewBinder,
    viewFlowBinder,
    viewPolicy,
    rejectQuote,
    attachStatus,
    removeDeclination,
    removeQuote,
    removeBinder,
    removePolicy,
    deleteSubmissionMarketRequest,
    changeToAwaitingQuoteViaPortal,
    changeToAwaitingBinder,
    removeInformationRequested,
    composeAdditionalInformationToMarketEmail,
    shareQuote,
    shareBinder,
    sharePolicy,
    markQuoteAsShared,
    markBinderAsShared,
    markPolicyAsShared,
    editPolicy,
    editBinder,
    changeToAwaitingPolicy,
    changeToIndicationQuote,
    changeToDeclined,
  } = useSubmissionMarketRequestActions(marketRequest, setIsSubmittingMarketRequest);

  const menuItems = {
    submitViaApi: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.submitViaAPI,
      onClick: submitProductViaApi,
      startAdornment: SettingsEthernetOutlined,
    },
    submitViaEmail: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.submitViaEmail,
      onClick: submitToMarketByEmail,
      startAdornment: EmailOutlined,
    },
    submitViaPortal: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.submitViaPortal,
      onClick: submitToMarketByPortal,
      startAdornment: Launch,
    },
    openPortal: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.openPortal,
      onClick: submitToMarketByPortal,
    },
    setAwaitingQuoteViaPortal: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.setAwaitingQuoteViaPortal,
      onClick: changeToAwaitingQuoteViaPortal,
    },
    deleteProduct: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.deleteProduct,
      onClick: () => deleteSubmissionMarketRequest(marketRequest.id),
      textColor: theme.palette.error.main,
      confirmationDialog: {
        message: messages.submissionWorkspace.marketRequestItem.dropDownItems.confirmDeleteProduct,
        title: messages.submissionWorkspace.marketRequestItem.dropDownItems.deleteProduct,
      },
      startAdornment: DeleteOutline,
    },
    setAwaitingQuote: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.setAwaitingQuote,
      onClick: changeToAwaitingQuote,
    },
    setAwaitingIndicationQuote: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.addQuoteIndication,
      onClick: changeToIndicationQuote,
    },
    attachQuote: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.attachQuote,
      onClick: attachQuote,
    },
    addInformationRequest: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.addInformationRequest,
      onClick: openInformationRequested,
    },
    updateInformationRequest: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.updateInformationRequest,
      onClick: openInformationRequested,
    },
    addDeclinationReason: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.addDeclinationReason,
      onClick: changeToDeclined,
    },
    sendAdditionalInformationToMarket: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.sendAdditionalInformationToMarket,
      onClick: composeAdditionalInformationToMarketEmail,
    },
    viewQuote: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.viewQuote,
      onClick: viewQuote,
    },
    attachBinder: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.attachBinder,
      onClick: attachStatus(QuoteFileIdType.Binder),
    },
    setAwaitingBinder: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.setAwaitingBinder,
      onClick: changeToAwaitingBinder,
    },
    setAwaitingPolicy: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.setAwaitingPolicy,
      onClick: changeToAwaitingPolicy,
    },
    shareQuote: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.shareQuote,
      onClick: shareQuote,
    },
    markQuoteAsShared: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.markQuoteAsShared,
      onClick: markQuoteAsShared,
    },
    editQuote: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.editQuote,
      onClick: editQuote,
    },
    rejectQuote: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.rejectQuote,
      onClick: rejectQuote,
    },
    removeQuote: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.removeQuote,
      onClick: removeQuote,
      confirmationDialog: {
        title: messages.submissionWorkspace.marketRequestItem.dropDownItems.removeQuote,
        message: messages.submissionWorkspace.marketRequestItem.dropDownItems.confirmRemoveQuote,
      },
    },
    editBinder: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.editBinder,
      onClick: editBinder,
    },

    viewBinder: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.viewBinder,
      onClick: viewBinder,
    },
    viewFlowBinder: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.viewBinder,
      onClick: viewFlowBinder,
    },
    shareBinder: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.shareBinder,
      onClick: shareBinder,
    },
    markBinderAsShared: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.markBinderAsShared,
      onClick: markBinderAsShared,
    },
    attachPolicy: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.attachPolicy,
      onClick: attachStatus(QuoteFileIdType.Policy),
    },
    editPolicy: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.editPolicy,
      onClick: editPolicy,
    },
    removeBinder: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.removeBinder,
      onClick: removeBinder,
      confirmationDialog: {
        title: messages.submissionWorkspace.marketRequestItem.dropDownItems.removeBinder,
        message: messages.submissionWorkspace.marketRequestItem.dropDownItems.confirmRemoveBinder,
      },
    },
    removeDeclination: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.removeDeclination,
      onClick: removeDeclination,
      confirmationDialog: {
        title: messages.submissionWorkspace.marketRequestItem.dropDownItems.removeDeclination,
        message: messages.submissionWorkspace.marketRequestItem.dropDownItems.confirmRemoveDeclination,
      },
    },
    viewPolicy: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.viewPolicy,
      onClick: viewPolicy,
    },
    sharePolicy: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.sharePolicy,
      onClick: sharePolicy,
    },
    markPolicyAsShared: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.markPolicyAsShared,
      onClick: markPolicyAsShared,
    },
    removePolicy: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.removePolicy,
      onClick: removePolicy,
      confirmationDialog: {
        title: messages.submissionWorkspace.marketRequestItem.dropDownItems.removePolicy,
        message: messages.submissionWorkspace.marketRequestItem.dropDownItems.confirmRemovePolicy,
      },
    },
    removeInformationRequested: {
      label: messages.submissionWorkspace.marketRequestItem.dropDownItems.removeInformationRequest,
      onClick: removeInformationRequested,
    },
  };

  function getAvailableSubmissionMethods(): MenuItemProps[] {
    const items: MenuItemProps[] = [];
    if (
      submission &&
      marketRequest.insuranceProduct?.externalProductId &&
      marketRequest.submissionMethod !== SubmissionMethod.API // Do not allow to submit via api twice
    ) {
      const validation = areHeraldProductsEligibleToCreateSubmission(
        [marketRequest.insuranceProduct?.externalProductId],
        submission.heraldData,
      );

      items.push({
        ...menuItems.submitViaApi,
        disabled: !validation.eligible,
        disabledTooltipContent: validation.reason,
      });
    }
    items.push(menuItems.submitViaEmail);

    if (marketRequest.insuranceProduct?.submissionPortal) {
      items.push(menuItems.submitViaPortal);
    }

    return items;
  }

  const createNestedSection = (label: string, items: MenuItemProps[]): MenuItemProps => ({
    label,
    subMenu: [{ key: lowerCase(label), items }],
  });

  const hasFlowQuote = !isEmpty(marketRequest.quote?.capitolaQuoteFileIds);
  const hasBinder = !isEmpty(marketRequest.quote?.binderFileIds);
  const hasFlowBinder = !isEmpty(marketRequest.quote?.flowBinderFileIds);
  const viewBinderAction = hasFlowBinder ? menuItems.viewFlowBinder : menuItems.viewBinder;

  const getStatusBasedActions = (status: SubmissionMarketRequestStatus): MenuItemProps[] => {
    const config: Record<SubmissionMarketRequestStatus, (MenuItemProps | undefined)[]> = {
      [SubmissionMarketRequestStatus.ReadyToMarket]: compact([
        ...getAvailableSubmissionMethods(),
        createNestedSection(
          messages.submissionWorkspace.marketRequestItem.dropDownItems.more,
          compact([
            menuItems.setAwaitingQuote,
            marketRequest.insuranceProduct?.submissionPortal ? menuItems.setAwaitingQuoteViaPortal : undefined,
            !marketRequest.quoteId && menuItems.attachQuote,
            menuItems.setAwaitingIndicationQuote,
            isEmpty(marketRequest.informationRequested)
              ? menuItems.addInformationRequest
              : menuItems.updateInformationRequest,
            menuItems.addDeclinationReason,
            menuItems.deleteProduct,
          ]),
        ),
      ]),
      [SubmissionMarketRequestStatus.AwaitingQuote]: compact([
        !marketRequest.quoteId && menuItems.attachQuote,
        isEmpty(marketRequest.informationRequested)
          ? menuItems.addInformationRequest
          : menuItems.updateInformationRequest,
        menuItems.setAwaitingIndicationQuote,
        menuItems.addDeclinationReason,
        createNestedSection(
          messages.submissionWorkspace.marketRequestItem.dropDownItems.more,
          compact([
            createNestedSection(
              messages.submissionWorkspace.marketRequestItem.dropDownItems.resubmit,
              compact([...getAvailableSubmissionMethods()]),
            ),
            marketRequest.insuranceProduct?.submissionPortal && menuItems.setAwaitingQuoteViaPortal,
            menuItems.deleteProduct,
          ]),
        ),
      ]),
      [SubmissionMarketRequestStatus.Indication]: compact([
        !marketRequest.quoteId && menuItems.attachQuote,
        marketRequest.quoteId && menuItems.editQuote,
        isEmpty(marketRequest.informationRequested)
          ? menuItems.addInformationRequest
          : menuItems.updateInformationRequest,
        menuItems.addDeclinationReason,
        createNestedSection(
          messages.submissionWorkspace.marketRequestItem.dropDownItems.more,
          compact([
            createNestedSection(
              messages.submissionWorkspace.marketRequestItem.dropDownItems.resubmit,
              compact([...getAvailableSubmissionMethods()]),
            ),
            marketRequest.insuranceProduct?.submissionPortal && menuItems.setAwaitingQuoteViaPortal,
            menuItems.deleteProduct,
          ]),
        ),
      ]),
      [SubmissionMarketRequestStatus.QuoteReady]: compact([
        hasFlowQuote && menuItems.viewQuote,
        menuItems.editQuote,
        menuItems.setAwaitingIndicationQuote,
        hasFlowQuote && menuItems.shareQuote,
        hasFlowQuote && !marketRequest.quote?.sharingDetails?.quoteShared && menuItems.markQuoteAsShared,
        menuItems.rejectQuote,
        submission?.placementType === PlacementType.Direct && !hasFlowQuote && menuItems.attachPolicy,
        hasFlowQuote &&
          createNestedSection(
            messages.submissionWorkspace.marketRequestItem.dropDownItems.bindingAndPolicy,
            compact([
              menuItems.setAwaitingBinder,
              menuItems.attachBinder,
              menuItems.setAwaitingPolicy,
              menuItems.attachPolicy,
            ]),
          ),
        createNestedSection(
          messages.submissionWorkspace.marketRequestItem.dropDownItems.more,
          compact([
            menuItems.removeQuote,
            menuItems.addDeclinationReason,
            isEmpty(marketRequest.informationRequested)
              ? menuItems.addInformationRequest
              : menuItems.updateInformationRequest,
          ]),
        ),
      ]),
      [SubmissionMarketRequestStatus.AwaitingBinder]: compact([
        menuItems.viewQuote,
        menuItems.editQuote,
        menuItems.attachBinder,
        menuItems.attachPolicy,
        createNestedSection(
          messages.submissionWorkspace.marketRequestItem.dropDownItems.more,
          compact([
            menuItems.removeQuote,
            menuItems.rejectQuote,
            isEmpty(marketRequest.informationRequested)
              ? menuItems.addInformationRequest
              : menuItems.updateInformationRequest,
            menuItems.addDeclinationReason,
          ]),
        ),
      ]),
      [SubmissionMarketRequestStatus.Bound]: compact([
        menuItems.viewQuote,
        viewBinderAction,
        menuItems.editBinder,
        menuItems.shareBinder,
        !marketRequest.quote?.sharingDetails?.binderShared && menuItems.markBinderAsShared,
        menuItems.setAwaitingPolicy,
        menuItems.attachPolicy,
        createNestedSection(
          messages.submissionWorkspace.marketRequestItem.dropDownItems.more,
          compact([menuItems.removeBinder]),
        ),
      ]),
      [SubmissionMarketRequestStatus.Declined]: compact([
        menuItems.removeDeclination,
        createNestedSection(
          messages.submissionWorkspace.marketRequestItem.dropDownItems.more,
          compact([menuItems.deleteProduct]),
        ),
      ]),
      [SubmissionMarketRequestStatus.PolicyIssued]: compact([
        menuItems.viewQuote,
        viewBinderAction,
        menuItems.shareBinder,
        menuItems.viewPolicy,
        menuItems.editPolicy,
        menuItems.sharePolicy,
        !marketRequest.quote?.sharingDetails?.policyShared && menuItems.markPolicyAsShared,
        createNestedSection(
          messages.submissionWorkspace.marketRequestItem.dropDownItems.more,
          compact([menuItems.removePolicy]),
        ),
      ]),
      [SubmissionMarketRequestStatus.QuoteNotAccepted]: compact([
        menuItems.editQuote,
        menuItems.viewQuote,
        createNestedSection(
          messages.submissionWorkspace.marketRequestItem.dropDownItems.more,
          compact([
            menuItems.removeQuote,
            !isEmpty(marketRequest.informationRequested) && menuItems.removeInformationRequested,
            menuItems.addDeclinationReason,
            isEmpty(marketRequest.informationRequested)
              ? menuItems.addInformationRequest
              : menuItems.updateInformationRequest,
          ]),
        ),
      ]),
      [SubmissionMarketRequestStatus.AwaitingPolicy]: compact([
        menuItems.viewQuote,
        !hasBinder && menuItems.editQuote,
        !hasBinder && menuItems.shareQuote,
        !hasBinder && !marketRequest.quote?.sharingDetails?.quoteShared && menuItems.markQuoteAsShared,
        hasBinder && viewBinderAction,
        hasBinder && menuItems.editBinder,
        hasBinder && menuItems.shareBinder,
        hasBinder && !marketRequest.quote?.sharingDetails?.binderShared && menuItems.markBinderAsShared,
        menuItems.attachPolicy,
        hasBinder && menuItems.removeBinder,
        ...(!hasBinder
          ? [
              createNestedSection(
                messages.submissionWorkspace.marketRequestItem.dropDownItems.more,
                compact([
                  menuItems.removeQuote,
                  menuItems.rejectQuote,
                  isEmpty(marketRequest.informationRequested)
                    ? menuItems.addInformationRequest
                    : menuItems.updateInformationRequest,
                  menuItems.addDeclinationReason,
                ]),
              ),
            ]
          : []),
      ]),
    };

    return compact(config[status] ?? []);
  };

  const getBorBasedActions = (status: SubmissionMarketRequestStatus): MenuItemProps[] => {
    const config: Record<SubmissionMarketRequestStatus, (MenuItemProps | undefined)[]> = {
      [SubmissionMarketRequestStatus.AwaitingPolicy]: [menuItems.attachPolicy],
      [SubmissionMarketRequestStatus.PolicyIssued]: [menuItems.viewPolicy, menuItems.removePolicy],
      [SubmissionMarketRequestStatus.Bound]: [menuItems.removePolicy],
      [SubmissionMarketRequestStatus.Declined]: [],
      [SubmissionMarketRequestStatus.QuoteNotAccepted]: [],
      [SubmissionMarketRequestStatus.QuoteReady]: [],
      [SubmissionMarketRequestStatus.AwaitingBinder]: [],
      [SubmissionMarketRequestStatus.Indication]: [],
      [SubmissionMarketRequestStatus.AwaitingQuote]: [],
      [SubmissionMarketRequestStatus.ReadyToMarket]: [],
    };

    return compact(config[status] ?? []);
  };

  const actions =
    submission?.type === SubmissionType.BOR
      ? getBorBasedActions(marketRequest.status)
      : getStatusBasedActions(marketRequest.status);

  return compact([
    {
      key: messages.submissionWorkspace.marketRequestItem.dropDownItems.actionsSection,
      items: actions ?? [],
    },
  ]);
}
